// src/pages/Warehouses/Reports/ProductMovementReport.jsx
// تقرير حركة الأصناف التفصيلي — كل حركة وارد/صادر مع الرصيد التراكمي

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Autocomplete, TextField, Typography, Box } from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import GridOnIcon from "@mui/icons-material/GridOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

export default function ProductMovementReport() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [product, setProduct] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [stockId, setStockId] = useState("");
  const [direction, setDirection] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setProductsLoading(true);
    Promise.all([
      axios.get(`/Product/by-company/${companyId}`),
      axios.get(`/Stock/by-company/${companyId}`),
    ])
      .then(([p, s]) => { setProducts(p.data || []); setStocks(s.data || []); })
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, [companyId]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const productIdOf = (p) => p?.id ?? p?.productId;

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const pid = productIdOf(product);
      const params = {
        ...(pid && { productId: pid }),
        ...(branchId && { branchId }),
        ...(stockId && { stockId }),
        dateFrom: `${range.dateFrom}T00:00:00`,
        dateTo: `${range.dateTo}T23:59:59`,
      };
      const res = await axios.get(`/StockMovements/report/${companyId}`, { params });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((m, i) => ({
        id: m.id ?? i + 1,
        productName: m.productName,
        stockName: m.stockName,
        branchId: m.branchId,
        movementDate: m.movementDate,
        invoiceNumber: m.invoiceNumber || "—",
        invoiceTypeName: m.invoiceTypeName || "—",
        quantityIn: Number(m.quantityIn) || 0,
        quantityOut: Number(m.quantityOut) || 0,
        unitPrice: Number(m.unitPrice) || 0,
        totalCost: Number(m.totalCost) || 0,
        runningBalance: Number(m.runningBalance) || 0,
      })));
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, product, branchId, stockId, range.dateFrom, range.dateTo]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (direction === "in" && r.quantityIn <= 0) return false;
      if (direction === "out" && r.quantityOut <= 0) return false;
      if (q && !(
        (r.productName ?? "").toLowerCase().includes(q) ||
        String(r.invoiceNumber).toLowerCase().includes(q) ||
        (r.invoiceTypeName ?? "").toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [rows, search, direction]);

  const stats = useMemo(() => {
    const t = visibleRows.reduce((a, r) => ({
      qtyIn: a.qtyIn + r.quantityIn,
      qtyOut: a.qtyOut + r.quantityOut,
      // التكلفة تُنسب لاتجاه الحركة بدل جمع الوارد والصادر معًا
      costIn: a.costIn + (r.quantityIn > 0 ? r.totalCost : 0),
      costOut: a.costOut + (r.quantityOut > 0 ? r.totalCost : 0),
    }), { qtyIn: 0, qtyOut: 0, costIn: 0, costOut: 0 });
    // الرصيد الختامي له معنى فقط عند اختيار صنف واحد (آخر رصيد تراكمي)
    const closing = product && rows.length ? rows[rows.length - 1].runningBalance : null;
    return { ...t, net: t.qtyIn - t.qtyOut, closing };
  }, [visibleRows, rows, product]);

  const columns = useMemo(() => [
    {
      field: "movementDate", headerName: "التاريخ", width: 130, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Box sx={{ textAlign: "center", lineHeight: 1.3 }}>
          <Typography sx={{ direction: "ltr", fontSize: 13.5, fontWeight: 600 }}>{dayjs(p.value).format("YYYY-MM-DD")}</Typography>
          <Typography sx={{ direction: "ltr", fontSize: 11.5, color: "#94a3b8" }}>{dayjs(p.value).format("HH:mm")}</Typography>
        </Box>
      ),
    },
    {
      field: "productName", headerName: "الصنف", flex: 1.5, minWidth: 180, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || "—"}</Typography>,
    },
    {
      field: "invoiceNumber", headerName: "رقم المستند", width: 120, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 13.5, color: "#4f46e5" }}>{p.value}</Typography>,
    },
    {
      field: "invoiceTypeName", headerName: "نوع الحركة", width: 130, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Box sx={{ px: 1.1, py: .3, borderRadius: "999px", fontSize: 12, fontWeight: 700, bgcolor: "#f1f5f9", color: "#334155" }}>
          {p.value}
        </Box>
      ),
    },
    {
      field: "quantityIn", headerName: "وارد", type: "number", width: 90, headerAlign: "center", align: "center",
      renderCell: (p) => p.value > 0
        ? <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5, color: "#047857" }}>+{fmtNumber(p.value)}</Typography>
        : <Typography sx={{ color: "#cbd5e1" }}>—</Typography>,
    },
    {
      field: "quantityOut", headerName: "صادر", type: "number", width: 90, headerAlign: "center", align: "center",
      renderCell: (p) => p.value > 0
        ? <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5, color: "#be123c" }}>−{fmtNumber(p.value)}</Typography>
        : <Typography sx={{ color: "#cbd5e1" }}>—</Typography>,
    },
    {
      field: "runningBalance", headerName: "الرصيد", type: "number", width: 105, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const v = Number(p.value) || 0;
        return (
          <Box sx={{
            px: 1.25, py: .3, borderRadius: "8px", fontWeight: 800, fontSize: 13.5, direction: "ltr", minWidth: 48, textAlign: "center",
            bgcolor: v > 0 ? "#d1fae5" : v < 0 ? "#ffe4e6" : "#f1f5f9",
            color: v > 0 ? "#047857" : v < 0 ? "#be123c" : "#94a3b8",
          }}>
            {fmtNumber(v)}
          </Box>
        );
      },
    },
    {
      field: "unitPrice", headerName: "السعر", type: "number", width: 105, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontSize: 13.5, color: "#475569" }}>{fmtMoney(p.value)}</Typography>,
    },
    {
      field: "totalCost", headerName: "الإجمالي", type: "number", width: 120, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5 }}>{fmtMoney(p.value)}</Typography>,
    },
    { field: "stockName", headerName: "المستودع", width: 120, headerAlign: "center", align: "center" },
    {
      field: "branchId", headerName: "الفرع", width: 120, headerAlign: "center", align: "center",
      // ✅ اسم الفرع بدل رقمه
      renderCell: (p) => <Typography sx={{ fontSize: 13.5 }}>{branchName(p.value) ?? p.value ?? "—"}</Typography>,
    },
  ], [branchName]);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info("لا توجد بيانات للتصدير");
    const data = visibleRows.map(r => ({
      "التاريخ": dayjs(r.movementDate).format("YYYY-MM-DD HH:mm"),
      "الصنف": r.productName,
      "رقم المستند": r.invoiceNumber,
      "نوع الحركة": r.invoiceTypeName,
      "وارد": r.quantityIn,
      "صادر": r.quantityOut,
      "الرصيد": r.runningBalance,
      "السعر": r.unitPrice,
      "الإجمالي": r.totalCost,
      "المستودع": r.stockName,
      "الفرع": branchName(r.branchId) ?? r.branchId,
    }));
    data.push({ "التاريخ": "الإجمالي", "وارد": stats.qtyIn, "صادر": stats.qtyOut });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!views"] = [{ RTL: true }];
    ws["!cols"] = Object.keys(data[0]).map(k => ({ wch: k === "الصنف" ? 30 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "حركة الأصناف");
    XLSX.writeFile(wb, `ProductMovements_${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setStockId("");
    setProduct(null);
    setDirection("");
    setSearch("");
  };

  const bName = branchName(branchId);
  const activeCount = (branchId ? 1 : 0) + (stockId ? 1 : 0) + (product ? 1 : 0) + (direction ? 1 : 0) + (search ? 1 : 0);

  return (
    <ReportPage
      title="حركة الأصناف التفصيلي"
      subtitle="كل حركة وارد وصادر للأصناف مع الرصيد التراكمي"
      icon={<TimelineIcon />}
      period={range}
      badges={[
        ...(product ? [{ label: product.arabicName || product.nameAr, icon: <Inventory2OutlinedIcon /> }] : []),
        ...(bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []),
      ]}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: "عدد الحركات", value: fmtNumber(visibleRows.length), icon: <FormatListNumberedIcon />, color: "primary" },
        { label: "إجمالي الوارد", value: fmtNumber(stats.qtyIn), icon: <SouthWestIcon />, color: "success" },
        { label: "إجمالي الصادر", value: fmtNumber(stats.qtyOut), icon: <NorthEastIcon />, color: "error" },
        { label: "صافي الحركة", value: `${stats.net > 0 ? "+" : ""}${fmtNumber(stats.net)}`, icon: <CompareArrowsIcon />, color: "slate" },
        { label: "تكلفة الوارد", value: fmtMoney(stats.costIn), icon: <PaymentsOutlinedIcon />, color: "info" },
        { label: "تكلفة الصادر", value: fmtMoney(stats.costOut), icon: <PaymentsOutlinedIcon />, color: "warning" },
        ...(stats.closing !== null
          ? [{ label: "الرصيد الختامي", value: fmtNumber(stats.closing), icon: <Inventory2OutlinedIcon />, color: "success", highlight: true }]
          : []),
      ]}
      filters={
        <ReportFilters
          range={range}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="الصنف، رقم المستند، النوع..."
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={activeCount}
        >
          <Autocomplete
            size="small"
            options={products}
            value={product}
            loading={productsLoading}
            loadingText="جاري التحميل..."
            onChange={(_, v) => setProduct(v)}
            getOptionLabel={(p) => p?.arabicName || p?.nameAr || ""}
            isOptionEqualToValue={(a, b) => productIdOf(a) === productIdOf(b)}
            noOptionsText="لا توجد نتائج"
            renderOption={(props, option) => (
              <li {...props} key={productIdOf(option)}>
                <Box>
                  <Typography sx={{ fontSize: 14 }}>{option.arabicName}</Typography>
                  {option.code && <Typography sx={{ fontSize: 12, color: "#64748b" }}>كود: {option.code}</Typography>}
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="الصنف" placeholder="الكل" sx={fieldSx} />}
          />
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="المستودع" value={stockId} onChange={setStockId}
            options={stocks.map(s => ({ value: String(s.id), label: s.nameAr || s.name }))}
          />
          <ReportSelect
            label="الاتجاه" value={direction} onChange={setDirection}
            options={[{ value: "in", label: "وارد فقط" }, { value: "out", label: "صادر فقط" }]}
          />
        </ReportFilters>
      }
    >
      <ReportTableCard title="الحركات" count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100, 200]}
          disableRowSelectionOnClick
          getRowHeight={() => 52}
          localeText={{
            noRowsLabel: "لا توجد حركات في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={reportGridSx}
        />
      </ReportTableCard>
    </ReportPage>
  );
}